import React, { useState } from 'react';
import TrustLayout from './TrustLayout';
import { apiRequest } from '../../services/apiClient';
import { PRODUCT_NAME } from '../../config/appBranding';

/**
 * Public support contact page. The form POSTs to /api/support which
 * lands a row in a support_tickets table and fans out to the support
 * inbox. Works without authentication.
 */
export default function SupportPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({ kind: 'idle' });

  async function handleSubmit(event) {
    event.preventDefault();
    if (!email.trim() || !message.trim()) {
      setSubmitState({
        kind: 'error',
        message: 'Email and a short message are required so we can reach you.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ kind: 'idle' });

    try {
      await apiRequest('/api/support', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          message: message.trim()
        })
      });
      setSubmitState({
        kind: 'success',
        message: "Thanks — we'll get back to you within one business day."
      });
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
    } catch (err) {
      setSubmitState({
        kind: 'error',
        message: err?.message || "Something went wrong on our side. Email support@vizionops.com and we'll make it right."
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <TrustLayout title="Support" eyebrow="Trust">
      <p>
        The fastest way to reach the {PRODUCT_NAME} team is email:{' '}
        <a href="mailto:support@vizionops.com">support@vizionops.com</a>. We read every message and reply within one business day.
      </p>

      <h2>Demo and sales</h2>
      <p>
        For demos, pricing, and pilot conversations, email{' '}
        <a href="mailto:sales@vizionops.com">sales@vizionops.com</a>.
      </p>

      <h2>General contact</h2>
      <p>
        For general questions, partnerships, or introductions, email{' '}
        <a href="mailto:hello@vizionops.com">hello@vizionops.com</a>.
      </p>

      <h2>Security issues</h2>
      <p>
        If you believe you've found a security issue, please email{' '}
        <a href="mailto:security@vizionops.com">security@vizionops.com</a> instead. We triage these first.
      </p>

      <h2>Send us a note</h2>
      <p>
        Prefer a form? Tell us what you need and we'll take it from there.
      </p>

      <form className="support-form" onSubmit={handleSubmit}>
        <label>
          Your name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            autoComplete="name"
          />
        </label>

        <label>
          Email<span aria-hidden="true"> *</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@northstarops.com"
            autoComplete="email"
            required
          />
        </label>

        <label>
          Company
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Your organization"
            autoComplete="organization"
          />
        </label>

        <label>
          What can we help with?<span aria-hidden="true"> *</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Questions about pricing, a demo, a bug, anything."
            required
          />
        </label>

        {submitState.kind === 'success' && (
          <div className="support-form__message support-form__message--success" role="status">
            {submitState.message}
          </div>
        )}
        {submitState.kind === 'error' && (
          <div className="support-form__message support-form__message--error" role="alert">
            {submitState.message}
          </div>
        )}

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending…' : 'Send message'}
        </button>
      </form>

      <div className="trust__callout">
        <strong>Already a customer?</strong> Signed-in users can also reach us from inside the app &mdash; we&rsquo;ll include your account context automatically.
      </div>
    </TrustLayout>
  );
}
